import {FC, useCallback, useState} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {Button} from '@shared_web/components/core/button';
import {showAlert} from '@shared_web/components/core/notifications';
import {SvgIcon} from '@shared_web/components/core/svg_icon';

import {FormWrapper} from '@mavismart_frontend/components/common_styles';
import {InputCodeVerification} from '@mavismart_frontend/components/input_code_verification';
import {Translate, translate} from '@mavismart_frontend/components/translate';
import {icon_arrow_left} from '@mavismart_frontend/icons/icon_arrow_left';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, CIRCLE_ICON_CONTAINER} from '@mavismart_frontend/lib/constants';
import {initStores} from '@mavismart_frontend/lib/init_reset_store';
import {getUserLanguage} from '@mavismart_frontend/stores/account_store';
import {clearLoginInfo, useLoginInfo} from '@mavismart_frontend/stores/login_info_store';

export function addMinutes(date: string, minutes: number): string {
  const dateObj = new Date(date);
  dateObj.setMinutes(dateObj.getMinutes() + minutes);
  return dateObj.toISOString() as string;
}

export const AccountVerification: FC = () => {
  const [, navigate] = useLocation();
  const loginInfo = useLoginInfo();
  const userLanguage = getUserLanguage(undefined);
  const [validationCode, setValidationCode] = useState(['', '', '', '', '', '']);
  // const [shouldSendNewCode, setShouldSendNewCode] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  // const [until, setUntil] = useState<string | undefined>(loginInfo?.nextCodeAt);

  const handleCheck = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault();
      if (loginInfo?.email === undefined) {
        showAlert(translate('generic_error', userLanguage));
        return;
      }
      if (validationCode.join('').length !== 6) {
        showAlert(translate('enter_the_6_digits_sent', userLanguage));
        return;
      }
      try {
        setLoading(true);
        const account = await apiCall('POST /login', {
          email: loginInfo.email,
          code: validationCode.join(''),
        });
        navigate('/');
        clearLoginInfo();
        initStores({...account, language: account.language ?? userLanguage});
      } catch {
        showAlert(translate('verification_code_incorrect', userLanguage));
      } finally {
        setLoading(false);
      }
    },
    [validationCode, navigate, userLanguage, loginInfo]
  );

  // const sendNewCode = useCallback(async () => {
  //   if (until === undefined) {
  //     showAlert(translate('generic_error', userLanguage));
  //     return;
  //   }
  //   // if (!shouldSendNewCode) {
  //   //   showAlert(translate('wait_before_resending_code', userLanguage));
  //   //   return;
  //   // }

  //   await sleep(1000); // Simulate network delay
  //   setShouldSendNewCode(false);
  //   setUntil(addMinutes(until, 2));
  //   showSuccess(translate('send_new_code', userLanguage));
  // }, [userLanguage, until]);

  // const onFinish = useCallback(() => {
  //   setShouldSendNewCode(true);
  // }, []);

  const navigateToLogin = useCallback(() => {
    clearLoginInfo();
    navigate('/login');
  }, [navigate]);

  return (
    <FormWrapper onSubmit={handleCheck}>
      <LeftAlignedBlock>
        <WrapperArrowLeftSvgIcon>
          <SvgIcon colorHover="black" size={20} icon={icon_arrow_left} onClick={navigateToLogin} />
        </WrapperArrowLeftSvgIcon>
        <WrapperTitle>
          <Title>
            <Translate value={'verify_account'} />
          </Title>
          <Description>
            <Translate value={'enter_the_6_digits_sent'} />
          </Description>
        </WrapperTitle>
        <div>
          <InputCodeVerification
            validationCode={validationCode}
            setValidationCode={setValidationCode}
            onEnter={handleCheck}
          />
        </div>
      </LeftAlignedBlock>
      <div>
        <Button themeOverrides={BUTTON_THEME} expand type="submit" loading={loading}>
          <Translate value={'verify'} />
        </Button>
        {/* <div style={{display: 'flex', justifyContent: 'center', marginTop: '10px'}}>
          <StyledButton
            onClick={sendNewCode}
            $shouldSendNewCode={shouldSendNewCode}
            disabled={!shouldSendNewCode}
          >
            <Translate value={'resend_code'} />
          </StyledButton>
          {loginInfo?.nextCodeAt ? <CountDown until={until} onFinish={onFinish} /> : <></>}
        </div> */}
      </div>
    </FormWrapper>
  );
};

const LeftAlignedBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-top: env(safe-area-inset-top);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #333;
`;

const Description = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
  color: #666;
`;

const WrapperTitle = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const WrapperArrowLeftSvgIcon = styled(CIRCLE_ICON_CONTAINER)`
  margin-bottom: 30px;
`;

// const StyledButton = styled(ButtonAsLink)<{$shouldSendNewCode: boolean}>`
//   color: ${({$shouldSendNewCode}) => ($shouldSendNewCode ? 'black' : '#545F71')};
//   cursor: ${({$shouldSendNewCode}) => ($shouldSendNewCode ? 'pointer' : 'not-allowed')} !important;
//   text-decoration: none;
//   font-size: 0.9rem;
//   margin-right: 10px;

//   &:hover {
//     color: ${({$shouldSendNewCode}) => ($shouldSendNewCode ? 'black' : '#545F71')};
//     text-decoration: ${({$shouldSendNewCode}) => ($shouldSendNewCode ? 'underline' : 'none')};
//   }
// `;

AccountVerification.displayName = 'AccountVerification';
